/**
 * Zod-схемы запросов и ответов API — общие для api (валидация, OpenAPI) и web (типы).
 */
import {
  CHANNELS,
  DIRECTIONS,
  FORMATS,
  GATES,
  LETTER_LANGS,
  MASTER_CV_MAX,
  MATCHES,
  PREP_VERDICTS,
  REVIEW_KINDS,
  SOURCES,
  STAGE_KEYS,
  isDate,
  isTime,
} from '@jt/core';
import { z } from 'zod';

export const API_PREFIX = '/api';

/* ---------- примитивы ---------- */

export const StageSchema = z.enum(STAGE_KEYS);
export const SourceSchema = z.enum(SOURCES);
export const FormatSchema = z.enum(FORMATS);
export const ChannelSchema = z.enum(CHANNELS);
export const DirectionSchema = z.enum(DIRECTIONS);
export const LetterLangSchema = z.enum(LETTER_LANGS);

export const DateSchema = z.string().refine(isDate, 'Дата в формате ГГГГ-ММ-ДД');
export const TimeSchema = z.string().refine(isTime, 'Время в формате ЧЧ:ММ');
export const IdSchema = z.uuid();
export const VersionSchema = z.number().int().min(1);
/** Короткий текст: обрезаем пробелы, ограничиваем длину. */
const t = (max = 500) => z.string().trim().max(max);
const url = () => t(2000);

export const IdParams = z.object({ id: IdSchema });

export const ErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ErrorSchema>['error'];

export const OkSchema = z.object({ ok: z.literal(true) });

/* ---------- вход ---------- */

export const LoginBody = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  password: z.string().min(1).max(200),
  totp: z
    .string()
    .trim()
    .regex(/^\d{6}$/)
    .optional(),
});

export const RoleSchema = z.enum(['admin', 'user']);
export type Role = z.infer<typeof RoleSchema>;

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  name: z.string(),
  totpEnabled: z.boolean(),
  role: RoleSchema,
});

export const MeSchema = z.object({ user: UserSchema, csrfToken: z.string() });
export type Me = z.infer<typeof MeSchema>;

export const PasswordBody = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(10, 'Не короче 10 символов').max(200),
});

export const TotpSetupSchema = z.object({ secret: z.string(), otpauthUrl: z.string(), qrSvg: z.string() });
export const TotpCodeBody = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Шесть цифр'),
});
export const TotpDisableBody = z.object({
  password: z.string().min(1).max(200),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/),
});

/* ---------- сущности ---------- */

export const ContactSchema = z.object({
  id: IdSchema,
  name: z.string(),
  role: z.string(),
  email: z.string(),
  linkedinUrl: z.string(),
  telegram: z.string(),
  phone: z.string(),
  company: z.string(),
  version: z.number().int(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const OfferSchema = z.object({
  salaryK: z.number().nullable(),
  bonus: z.string(),
  format: FormatSchema.nullable(),
  contract: z.string(),
  startDate: DateSchema.nullable(),
  deadline: DateSchema.nullable(),
  benefits: z.string(),
  gut: z.number().int().min(1).max(5).nullable(),
  version: z.number().int(),
});
export type Offer = z.infer<typeof OfferSchema>;

export const ScoreSchema = z.object({
  value: z.number().nullable(),
  parts: z.array(z.object({ ok: z.boolean(), text: z.string() })),
});
export type Score = z.infer<typeof ScoreSchema>;

export const ActivitySchema = z.object({
  id: IdSchema,
  applicationId: IdSchema,
  occurredOn: DateSchema,
  channel: ChannelSchema,
  direction: DirectionSchema,
  text: z.string(),
  raw: z.string(),
  contactId: IdSchema.nullable(),
  externalRef: z.string().nullable(),
  externalUrl: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type Activity = z.infer<typeof ActivitySchema>;

export const FileKindSchema = z.enum(['resume', 'attachment', 'link']);
export const FileSchema = z.object({
  id: IdSchema,
  kind: FileKindSchema,
  name: z.string(),
  mime: z.string(),
  sizeBytes: z.number().int().nullable(),
  linkUrl: z.string(),
  lang: z.string(),
  note: z.string(),
  /** Файл загружен на сервер (иначе это ссылка). */
  stored: z.boolean(),
  /** К скольким вакансиям прикреплён. */
  usedCount: z.number().int(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type FileItem = z.infer<typeof FileSchema>;
export const AttachedFileSchema = FileSchema.extend({ attachedAt: z.string() });
export type AttachedFile = z.infer<typeof AttachedFileSchema>;

export const CardStatusSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('offer'), text: z.string(), date: z.string() }),
  z.object({ kind: z.literal('rejected'), text: z.string(), date: z.string() }),
  z.object({ kind: z.literal('closed'), stage: StageSchema, text: z.string(), date: z.string() }),
  z.object({ kind: z.literal('next'), tone: z.enum(['soon', 'later', 'late']), text: z.string() }),
  z.object({ kind: z.literal('reply'), text: z.string() }),
  z.object({ kind: z.literal('silence'), text: z.string() }),
  z.object({ kind: z.literal('plain'), stage: StageSchema, text: z.string(), date: z.string() }),
]);
export type CardStatus = z.infer<typeof CardStatusSchema>;

export const ApplicationSchema = z.object({
  id: IdSchema,
  company: z.string(),
  role: z.string(),
  url: z.string(),
  source: SourceSchema.nullable(),
  location: z.string(),
  format: FormatSchema.nullable(),
  salaryRange: z.string(),
  appliedAt: DateSchema.nullable(),
  stage: StageSchema,
  maxStage: StageSchema,
  stageAt: DateSchema.nullable(),
  nextStep: z.string(),
  nextDate: DateSchema.nullable(),
  nextTime: TimeSchema.nullable(),
  rejectionReason: z.string(),
  notes: z.string(),
  isDemo: z.boolean(),
  position: z.number(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type Application = z.infer<typeof ApplicationSchema>;

export const ApplicationListItemSchema = ApplicationSchema.extend({
  contacts: z.array(ContactSchema),
  offer: OfferSchema.nullable(),
  card: CardStatusSchema,
  lastActivityOn: DateSchema,
  needsReply: z.boolean(),
  /** Последнее несистемное сообщение — для списка «Ждут вашего ответа». */
  lastMessage: z
    .object({ occurredOn: z.string(), channel: ChannelSchema, direction: DirectionSchema, text: z.string() })
    .nullable(),
});
export type ApplicationListItem = z.infer<typeof ApplicationListItemSchema>;

/** Контакт, найденный в интернете: ждёт подтверждения пользователя. */
export const ContactSuggestionSchema = z.object({
  name: z.string(),
  role: z.string(),
  linkedinUrl: z.string(),
  email: z.string(),
  telegram: z.string(),
  sourceUrl: z.string(),
  why: z.string(),
});
export type ContactSuggestion = z.infer<typeof ContactSuggestionSchema>;
export const ContactSearchSchema = z.object({
  status: z.enum(['running', 'done', 'error']),
  at: z.string(),
  results: z.array(ContactSuggestionSchema),
  note: z.string(),
  error: z.string().optional(),
});
export type ContactSearchState = z.infer<typeof ContactSearchSchema>;

export const ApplicationFullSchema = ApplicationSchema.extend({
  contacts: z.array(ContactSchema),
  /** Поиск контакта в интернете; null — не искали. */
  contactSearch: ContactSearchSchema.nullable(),
  offer: OfferSchema.nullable(),
  score: ScoreSchema,
  activities: z.array(ActivitySchema),
  files: z.array(AttachedFileSchema),
  card: CardStatusSchema,
  needsReply: z.boolean(),
  lastActivityOn: DateSchema,
  lastMessage: ApplicationListItemSchema.shape.lastMessage,
});
export type ApplicationFull = z.infer<typeof ApplicationFullSchema>;

/* ---------- вакансии: запросы ---------- */

export const ApplicationsQuery = z.object({
  stage: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',') : undefined))
    .pipe(z.array(StageSchema).optional()),
  source: SourceSchema.optional(),
  q: t(200).optional(),
  /** true — только закрытые, false — только открытые. */
  archived: z.enum(['true', 'false']).optional(),
  demo: z.enum(['true', 'false']).optional(),
  /** true — корзина (мягко удалённые). */
  trash: z.enum(['true', 'false']).optional(),
  sort: z.enum(['urgency', 'updated']).default('urgency'),
});

const contactFields = {
  name: t(200),
  role: t(200),
  email: z.union([z.literal(''), z.email().max(320)]),
  linkedinUrl: url(),
  telegram: t(200),
  phone: t(100),
  company: t(200),
};

export const ContactInput = z.object({
  name: contactFields.name.default(''),
  role: contactFields.role.default(''),
  email: contactFields.email.default(''),
  linkedinUrl: contactFields.linkedinUrl.default(''),
  telegram: contactFields.telegram.default(''),
  phone: contactFields.phone.default(''),
  company: contactFields.company.default(''),
});
export type ContactInput = z.input<typeof ContactInput>;

const applicationFields = {
  company: t(200),
  role: t(200),
  url: url(),
  source: SourceSchema.nullable(),
  location: t(200),
  format: FormatSchema.nullable(),
  salaryRange: t(200),
  appliedAt: DateSchema.nullable(),
  nextStep: t(500),
  nextDate: DateSchema.nullable(),
  nextTime: TimeSchema.nullable(),
  rejectionReason: t(2000),
  notes: t(20000),
  position: z.number(),
};

export const CreateApplicationBody = z
  .object({
    company: applicationFields.company.default(''),
    role: applicationFields.role.default(''),
    url: applicationFields.url.default(''),
    source: applicationFields.source.default('LinkedIn'),
    location: applicationFields.location.default(''),
    format: applicationFields.format.default(null),
    salaryRange: applicationFields.salaryRange.default(''),
    appliedAt: applicationFields.appliedAt.optional(),
    stage: StageSchema.default('applied'),
    nextStep: applicationFields.nextStep.default(''),
    nextDate: applicationFields.nextDate.default(null),
    nextTime: applicationFields.nextTime.default(null),
    notes: applicationFields.notes.default(''),
    contact: ContactInput.optional(),
  })
  .refine((b) => b.company || b.role, { message: 'Укажите компанию или роль', path: ['company'] });
export type CreateApplicationInput = z.input<typeof CreateApplicationBody>;

export const PatchApplicationBody = z.object(applicationFields).partial().extend({ version: VersionSchema });
export type PatchApplicationInput = z.input<typeof PatchApplicationBody>;

export const StageBody = z.object({
  stage: StageSchema,
  source: z.literal('manual').default('manual'),
  version: VersionSchema.optional(),
});

export const OfferBody = z.object({
  salaryK: z.number().min(0).max(99999).nullable().optional(),
  bonus: t(500).optional(),
  format: FormatSchema.nullable().optional(),
  contract: t(200).optional(),
  startDate: DateSchema.nullable().optional(),
  deadline: DateSchema.nullable().optional(),
  benefits: t(5000).optional(),
  gut: z.number().int().min(1).max(5).nullable().optional(),
  version: VersionSchema.optional(),
});
export const OfferResponse = z.object({ offer: OfferSchema, score: ScoreSchema });

export const LinkContactBody = z.union([
  z.object({ contactId: IdSchema }),
  z.object({ contact: ContactInput }),
]);

// Без значений по умолчанию: в Zod 4 .partial() поверх .default('') подставил бы пустые строки.
export const ContactPatchBody = z.object(contactFields).partial().extend({ version: VersionSchema });
export const ContactsQuery = z.object({ q: t(200).optional() });
export const ContactWithAppsSchema = ContactSchema.extend({
  applications: z.array(
    z.object({ id: IdSchema, company: z.string(), role: z.string(), stage: StageSchema }),
  ),
});
export type ContactWithApps = z.infer<typeof ContactWithAppsSchema>;

export const ActivityBody = z.object({
  occurredOn: DateSchema,
  channel: ChannelSchema.default('Other'),
  direction: DirectionSchema.default('in'),
  text: t(5000).min(1, 'Опишите, что было'),
  raw: z.string().max(20000).default(''),
  contactId: IdSchema.nullable().default(null),
  externalUrl: url().nullable().default(null),
});
export const RecentActivitySchema = ActivitySchema.extend({
  company: z.string(),
  role: z.string(),
});
export type RecentActivity = z.infer<typeof RecentActivitySchema>;

export const ActivityPatchBody = z
  .object({
    occurredOn: DateSchema,
    channel: ChannelSchema,
    direction: DirectionSchema,
    text: t(5000),
    raw: z.string().max(20000),
    contactId: IdSchema.nullable(),
  })
  .partial()
  .extend({ version: VersionSchema });

export const FileLinkBody = z.object({
  name: t(300).default(''),
  url: url().min(1, 'Вставьте ссылку'),
  kind: z.enum(['resume', 'link']).default('link'),
  lang: t(50).default(''),
  note: t(300).default(''),
});
export const FilePatchBody = z
  .object({ name: t(300), lang: t(50), note: t(300) })
  .partial()
  .extend({ version: VersionSchema });
export const AttachFileBody = z.object({ fileId: IdSchema });
export const FilesQuery = z.object({ kind: FileKindSchema.optional() });

/* ---------- настройки и чек-лист ---------- */

export const NOTIFY_EVENTS = [
  'interview',
  'offer',
  'rejection',
  'message',
  'screening',
  'applied',
  /** Новые вакансии ленты «Вакансии» — карточкой в Telegram. */
  'feed',
] as const;

export const SettingsSchema = z.object({
  name: z.string(),
  about: z.string(),
  masterCv: z.string(),
  lettersLang: LetterLangSchema,
  timezone: z.string(),
  ghostDays: z.number().int(),
  minSalaryK: z.number().nullable(),
  targetSalaryK: z.number().nullable(),
  formats: z.array(FormatSchema),
  preferredContract: z.string(),
  noticePeriod: z.string(),
  availableFrom: DateSchema.nullable(),
  gmailFirstScanDays: z.number().int(),
  gmailExtraQuery: z.string(),
  gmailMoveLabel: z.string(),
  gmailScanIntervalMin: z.number().int(),
  gmailAutoApply: z.boolean(),
  autoGhost: z.boolean(),
  telegramLinked: z.boolean(),
  digestTime: TimeSchema.nullable(),
  /** Дни дайджеста: 0 — воскресенье … 6 — суббота. */
  digestDays: z.array(z.number().int().min(0).max(6)),
  notifyEvents: z.array(z.string()),
  aiMonthlyTokenLimit: z.number().int().nullable(),
  ui: z.record(z.string(), z.unknown()),
  version: z.number().int(),
});
export type Settings = z.infer<typeof SettingsSchema>;

const isTimeZone = (tz: string) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

export const SettingsPatchBody = z
  .object({
    name: t(200),
    about: t(2000),
    masterCv: t(MASTER_CV_MAX),
    lettersLang: LetterLangSchema,
    timezone: z.string().refine(isTimeZone, 'Неизвестный часовой пояс'),
    ghostDays: z.number().int().min(3).max(60),
    minSalaryK: z.number().min(0).max(9999).nullable(),
    targetSalaryK: z.number().min(0).max(9999).nullable(),
    formats: z.array(FormatSchema),
    preferredContract: t(200),
    noticePeriod: t(200),
    availableFrom: DateSchema.nullable(),
    gmailFirstScanDays: z.number().int().min(3).max(90),
    gmailExtraQuery: t(1000),
    gmailMoveLabel: t(100),
    gmailScanIntervalMin: z.number().int().min(5).max(120),
    gmailAutoApply: z.boolean(),
    autoGhost: z.boolean(),
    digestTime: TimeSchema.nullable(),
    digestDays: z
      .array(z.number().int().min(0).max(6))
      .max(7)
      .transform((d) => [...new Set(d)].sort()),
    notifyEvents: z.array(z.enum(NOTIFY_EVENTS)),
    aiMonthlyTokenLimit: z.number().int().min(0).nullable(),
    ui: z.record(z.string(), z.unknown()),
  })
  .partial()
  .extend({ version: VersionSchema });
export type SettingsPatch = z.input<typeof SettingsPatchBody>;

export const ReadinessItemSchema = z.object({
  id: IdSchema,
  text: z.string(),
  done: z.boolean(),
  position: z.number().int(),
  version: z.number().int(),
});
export type ReadinessItem = z.infer<typeof ReadinessItemSchema>;
export const ReadinessBody = z.object({ text: t(500).min(1) });
export const ReadinessPatchBody = z
  .object({ text: t(500).min(1), done: z.boolean() })
  .partial()
  .extend({ version: VersionSchema });
export const ReorderBody = z.object({ ids: z.array(IdSchema).min(1) });

/* ---------- обзор ---------- */

export const TaskSchema = z.object({
  applicationId: IdSchema,
  urgency: z.number(),
  icon: z.enum(['trophy', 'calendar', 'mail', 'bell', 'clock']),
  tone: z.enum(['', 'g', 'v', 'o']),
  title: z.string(),
  sub: z.string(),
  late: z.boolean(),
  action: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('nav'), label: z.string(), view: z.literal('offers') }),
    z.object({ kind: z.literal('draft'), label: z.string(), draft: z.enum(['prep', 'reply', 'followup']) }),
    z.object({ kind: z.literal('done'), label: z.string() }),
    z.object({ kind: z.literal('open'), label: z.string() }),
    z.object({ kind: z.literal('ghost'), label: z.string() }),
  ]),
});
export type Task = z.infer<typeof TaskSchema>;

export const KpisSchema = z.object({
  active: z.number().int(),
  waiting: z.number().int(),
  interviews: z.number().int(),
  offers: z.number().int(),
  rejected: z.number().int(),
});

export const TodaySchema = z.object({
  today: DateSchema,
  tasks: z.array(TaskSchema),
  pendingInbox: z.number().int(),
  needsReply: z.number().int(),
  kpis: KpisSchema,
  ai: z.boolean(),
  /** Поиск контакта в интернете включён (кнопка в карточке). */
  contactSearch: z.boolean(),
  /** Новых вакансий в ленте «Вакансии» — счётчик у пункта меню. */
  feedNew: z.number().int(),
});
export type Today = z.infer<typeof TodaySchema>;

export const StatsSchema = z.object({
  total: z.number().int(),
  respondedPct: z.number(),
  avgFirstResponseDays: z.number().nullable(),
  funnel: z.array(z.object({ stage: StageSchema, count: z.number().int(), pct: z.number() })),
  bySource: z.array(
    z.object({
      source: z.string(),
      count: z.number().int(),
      respondedPct: z.number(),
      interviews: z.number().int(),
      offers: z.number().int(),
    }),
  ),
  rejections: z.array(
    z.object({
      id: IdSchema,
      company: z.string(),
      role: z.string(),
      reached: StageSchema,
      stageAt: z.string().nullable(),
      reason: z.string(),
    }),
  ),
});
export type Stats = z.infer<typeof StatsSchema>;

export const OffersCompareSchema = z.object({
  offers: z.array(
    z.object({
      application: ApplicationSchema,
      offer: OfferSchema.nullable(),
      score: ScoreSchema,
      /** Дней до дедлайна, null — дедлайна нет. */
      daysLeft: z.number().int().nullable(),
    }),
  ),
  criteria: z.object({
    minSalaryK: z.number().nullable(),
    targetSalaryK: z.number().nullable(),
    formats: z.array(FormatSchema),
    preferredContract: z.string(),
  }),
});
export type OffersCompare = z.infer<typeof OffersCompareSchema>;

export const DemoResponse = z.object({ count: z.number().int() });

/* ---------- интеграции ---------- */

export const JobRunSchema = z.object({
  name: z.string(),
  lastStartedAt: z.string().nullable(),
  lastFinishedAt: z.string().nullable(),
  ok: z.boolean().nullable(),
  message: z.string(),
});

export const IntegrationsStatusSchema = z.object({
  gmail: z.object({
    /** Чем подключена почта этого пользователя; null — не подключена. */
    provider: z.enum(['gmail_api', 'imap']).nullable(),
    /** Какой это ящик: Gmail или Mail.ru (по адресу); null — не подключена. */
    service: z.enum(['gmail', 'mailru']).nullable(),
    /** Администратор задал OAuth-клиент Google — можно войти через Google. */
    googleAvailable: z.boolean(),
    connected: z.boolean(),
    email: z.string().nullable(),
    lastScanAt: z.string().nullable(),
    lastError: z.string().nullable(),
    needsReauth: z.boolean(),
  }),
  telegram: z.object({ configured: z.boolean(), linked: z.boolean(), botUsername: z.string().nullable() }),
  /** Talanto (MCP): ключ пользователя, импорт откликов и сверка статусов. */
  talanto: z.object({
    connected: z.boolean(),
    tier: z.string().nullable(),
    periodEnd: z.string().nullable(),
    lastSyncAt: z.string().nullable(),
    lastError: z.string().nullable(),
    needsReauth: z.boolean(),
    /** Сколько откликов Talanto связано с вакансиями трекера. */
    linked: z.number().int(),
  }),
  claude: z.object({
    configured: z.boolean(),
    model: z.string(),
    tokensThisMonth: z.number().int(),
    monthlyLimit: z.number().int().nullable(),
  }),
  jobs: z.array(JobRunSchema),
});
export type IntegrationsStatus = z.infer<typeof IntegrationsStatusSchema>;

/* ---------- Ключи доступа (задаются в интерфейсе, значения наружу не отдаются) ---------- */

/** Ключи сервисов на весь сервер — задаёт администратор. Почта у каждого пользователя своя. */
export const SECRET_NAMES = [
  'anthropicApiKey',
  'telegramBotToken',
  'telegramApiId',
  'telegramApiHash',
  'googleClientId',
  'googleClientSecret',
] as const;
export type SecretName = (typeof SECRET_NAMES)[number];

export const SecretStatusSchema = z.object({
  /** ui — задан в интерфейсе, env — в deploy/.env на сервере, null — не задан. */
  source: z.enum(['ui', 'env']).nullable(),
  /** Для секретов — только последние символы («…a1B2»); для обычных полей — значение целиком. */
  hint: z.string().nullable(),
});
export const SecretsStatusSchema = z.object({
  /** Можно ли сохранять ключи (на сервере задан APP_ENCRYPTION_KEY). */
  writable: z.boolean(),
  items: z.object(
    Object.fromEntries(SECRET_NAMES.map((n) => [n, SecretStatusSchema])) as Record<
      SecretName,
      typeof SecretStatusSchema
    >,
  ),
});
export type SecretsStatus = z.infer<typeof SecretsStatusSchema>;

/** Пустая строка — убрать значение из интерфейса (вернуться к deploy/.env). */
export const SecretsPatchBody = z
  .object({
    anthropicApiKey: z.string().trim().max(500),
    telegramBotToken: z.string().trim().max(200),
    /** Приложение на my.telegram.org — для чтения каналов от имени пользователей (лента «Вакансии»). */
    telegramApiId: z.string().trim().regex(/^\d*$/, 'api_id — это число').max(20),
    telegramApiHash: z
      .string()
      .trim()
      .regex(/^[0-9a-f]*$/i, 'api_hash — 32 символа 0–9 и a–f')
      .max(64),
    googleClientId: z.string().trim().max(300),
    googleClientSecret: z.string().trim().max(300),
  })
  .partial();
export type SecretsPatch = z.infer<typeof SecretsPatchBody>;

/* ---------- Claude ---------- */

const ParsedContactSchema = z.object({
  name: t(200),
  role: t(200),
  email: t(320),
  linkedinUrl: url(),
  telegram: t(200),
  phone: t(100),
});

export const ParsedOfferSchema = z.object({
  salaryK: z.number().min(0).max(99999).nullable(),
  bonus: t(500),
  format: FormatSchema.nullable(),
  contract: t(200),
  startDate: DateSchema.nullable(),
  deadline: DateSchema.nullable(),
  benefits: t(5000),
});

export const ParsedSchema = z.object({
  type: z.enum(['vacancy', 'message']),
  matchId: IdSchema.nullable(),
  company: t(200),
  role: t(200),
  url: url(),
  source: SourceSchema,
  location: t(200),
  format: FormatSchema.nullable(),
  salary: t(200),
  contact: ParsedContactSchema,
  channel: ChannelSchema,
  // sys — автоматическое подтверждение (отклик отправлен / просмотрен), не ждёт ответа
  direction: DirectionSchema,
  summary: t(5000),
  stage: StageSchema.nullable(),
  rejectionReason: t(2000),
  nextStep: t(500),
  nextDate: DateSchema.nullable(),
  nextTime: TimeSchema.nullable(),
  offer: ParsedOfferSchema.nullable(),
});
export type Parsed = z.infer<typeof ParsedSchema>;

export const ParseBody = z.object({
  text: z.string().trim().min(1, 'Вставьте текст').max(200_000),
  targetApplicationId: IdSchema.nullable().optional(),
});
export const ParseResponse = z.object({
  parsed: ParsedSchema,
  raw: z.string(),
  truncated: z.boolean(),
  /** Разбор по ссылке: описание вакансии со страницы — уходит в заметки новой вакансии. */
  description: z.string().optional(),
});
export const ParseUrlBody = z.object({
  url: z
    .string()
    .trim()
    .min(1, 'Вставьте ссылку')
    .max(2000)
    .regex(/^https?:\/\/\S+$/i, 'Нужна ссылка https://…'),
  targetApplicationId: IdSchema.nullable().optional(),
});
export type ParseResult = z.infer<typeof ParseResponse>;

/** Подтверждённый разбор: куда сохранить и отредактированные поля. */
export const ParseApplyBody = ParsedSchema.omit({ matchId: true, type: true }).extend({
  target: z.union([z.literal('new'), IdSchema]),
  raw: z.string().max(20000).default(''),
  /** Описание вакансии в заметки (если заметки вакансии пусты). */
  notes: z.string().max(10000).optional(),
  occurredOn: DateSchema.optional(),
});
export type ParseApply = z.input<typeof ParseApplyBody>;

export const DRAFT_KIND_KEYS = [
  'reply',
  'followup',
  'thanks',
  'prep',
  'accept',
  'negotiate',
  'decline',
  'feedback',
] as const;
export const DraftBody = z.object({
  applicationId: IdSchema,
  kind: z.enum(DRAFT_KIND_KEYS),
  lang: LetterLangSchema.optional(),
  extra: t(1000).default(''),
});
export const DraftLogBody = z.object({
  applicationId: IdSchema,
  kind: z.enum(DRAFT_KIND_KEYS),
  text: z.string().trim().min(1).max(20000),
});

/* ---------- «Входящие» ---------- */

export const InboxEventSchema = z.object({
  id: IdSchema,
  source: z.enum(['gmail', 'telegram', 'api']),
  kind: z.string(),
  receivedAt: z.string(),
  sender: z.string(),
  subject: z.string(),
  /** «NovaPay: приглашение на интервью, статус → «Техническое интервью»». */
  title: z.string(),
  /** Уведомление LinkedIn о сообщении: текста нет, его вставляют вручную из LinkedIn. */
  textInLinkedIn: z.boolean(),
  summary: z.string(),
  nextStep: z.string(),
  nextDate: z.string().nullable(),
  externalUrl: z.string().nullable(),
  targetApplicationId: IdSchema.nullable(),
  raw: z.string(),
  /** Разобранные поля — для «Изменить». */
  data: ParsedSchema,
  occurredOn: z.string(),
});
export type InboxEvent = z.infer<typeof InboxEventSchema>;

export const InboxApplyBody = z.object({ edits: ParseApplyBody.optional() });
export const InboxApplyAllResponse = z.object({ count: z.number().int() });

export const IngestBody = z.object({
  text: z.string().trim().min(1).max(200_000),
  source: z.enum(['api', 'n8n', 'telegram', 'email']).default('api'),
  externalId: z.string().trim().max(300).optional(),
});
export const IngestResponse = z.object({ id: IdSchema, created: z.boolean() });

export const ApiKeySchema = z.object({
  id: IdSchema,
  name: z.string(),
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export const ApiKeyCreated = ApiKeySchema.extend({ key: z.string() });
export const ApiKeyBody = z.object({ name: t(100).default('n8n') });

export const ScanResponse = z.object({ queued: z.boolean() });

/* ---------- Почта по IMAP (у каждого пользователя своя) ---------- */

export const ImapBody = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[^\s@]+@[^\s@]+$/, 'Адрес вида you@gmail.com или you@mail.ru')
    .max(320),
  /** Пароль приложения Google (16 букв) или пароль для внешнего приложения Mail.ru; пробелы убираются. */
  password: z
    .string()
    .max(200)
    .transform((v) => v.replace(/\s+/g, ''))
    .pipe(z.string().min(8, 'Пароль приложения — не короче 8 символов')),
});

/* ---------- Пользователи, приглашения, сброс пароля ---------- */

const PasswordSchema = z.string().min(10, 'Не короче 10 символов').max(200);

export const LinkInfoSchema = z.object({
  kind: z.enum(['invite', 'reset']),
  /** Приглашение на конкретную почту или почта пользователя при сбросе; '' — любая. */
  email: z.string(),
});
export const JoinBody = z.object({
  token: z.string().min(10).max(200),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[^\s@]+@[^\s@]+$/, 'Адрес вида you@example.com')
    .max(320),
  name: z.string().trim().max(200).default(''),
  password: PasswordSchema,
});
export const ResetBody = z.object({ token: z.string().min(10).max(200), password: PasswordSchema });
export const ForgotBody = z.object({ email: z.string().trim().toLowerCase().pipe(z.email('Адрес почты')) });
export const DeleteAccountBody = z.object({ password: z.string().min(1).max(200) });

export const CreatedLinkSchema = z.object({ url: z.string(), expiresAt: z.string() });
export const InviteBody = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(320)
    .refine((v) => !v || /^[^\s@]+@[^\s@]+$/.test(v), 'Адрес вида friend@example.com')
    .default(''),
});
export const InviteSchema = z.object({
  id: IdSchema,
  email: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
});

export const AdminUserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  blocked: z.boolean(),
  createdAt: z.string(),
  /** Последний вход (активность сессии). */
  lastSeenAt: z.string().nullable(),
  /** В сколько из последних 30 дней заходил. */
  activeDays30: z.number().int(),
  applications: z.number().int(),
  tokensThisMonth: z.number().int(),
  aiMonthlyTokenLimit: z.number().int().nullable(),
  filesBytes: z.number().int(),
  mail: z.enum(['gmail_api', 'imap']).nullable(),
  telegramLinked: z.boolean(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;
/** DAU / WAU / MAU и уникальные пользователи по дням за 30 дней. */
export const AdminActivitySchema = z.object({
  dau: z.number().int(),
  wau: z.number().int(),
  mau: z.number().int(),
  /** Всего пользователей — для доли активных. */
  users: z.number().int(),
  days: z.array(z.object({ day: z.string(), users: z.number().int() })),
});
export type AdminActivity = z.infer<typeof AdminActivitySchema>;
export const AdminUserPatchBody = z
  .object({
    role: RoleSchema,
    blocked: z.boolean(),
    aiMonthlyTokenLimit: z.number().int().min(0).nullable(),
  })
  .partial();

export const TalantoKeyBody = z.object({
  key: z.string().trim().min(10, 'Вставьте ключ из кабинета Talanto целиком').max(300),
});

/* ---------- Лента «Вакансии» ---------- */

export const FeedLevelSchema = z.enum(['junior', 'middle', 'senior', 'lead']);
export const FeedFilterSchema = z.object({
  keywords: z.array(z.string().trim().min(1).max(60)).max(60),
  exclude: z.array(z.string().trim().min(1).max(60)).max(60),
  levels: z.array(FeedLevelSchema),
  formats: z.array(FormatSchema),
  salaryMinUsd: z.number().int().min(0).max(100000).nullable(),
  includeNoSalary: z.boolean(),
  talantoFilterId: z.string().max(100).nullable(),
  himalayas: z.boolean().default(true),
  himalayasCountry: z
    .string()
    .trim()
    .regex(/^([A-Za-z]{2})?$/, 'Код страны из двух букв, например ES')
    .default('ES'),
  linkedin: z.boolean().default(true),
  linkedinLocation: z.string().trim().min(1, 'Укажите страну или город').max(100).default('Spain'),
  linkedinAlerts: z.boolean().default(true),
  djinni: z.boolean().default(true),
  justjoin: z.boolean().default(true),
  languages: z.array(z.enum(['ru', 'uk', 'en', 'es'])).default([]),
});
export type FeedFilterDto = z.infer<typeof FeedFilterSchema>;

export const FeedItemSchema = z.object({
  id: IdSchema,
  source: z.enum(['telegram', 'talanto', 'himalayas', 'linkedin', 'djinni', 'justjoin']),
  sourceTitle: z.string(),
  url: z.string(),
  postedAt: z.string(),
  company: z.string(),
  role: z.string(),
  level: z.string(),
  format: FormatSchema.nullable(),
  location: z.string(),
  salary: z.string(),
  text: z.string(),
  reason: z.string(),
  /** Та же вакансия в других каналах. */
  also: z.array(z.object({ sourceTitle: z.string(), url: z.string() })),
  status: z.enum(['new', 'saved', 'hidden']),
  applicationId: IdSchema.nullable(),
});
export type FeedItem = z.infer<typeof FeedItemSchema>;
export const FeedQuery = z.object({
  status: z.enum(['new', 'saved', 'hidden', 'all']).default('new'),
  source: z.enum(['telegram', 'talanto', 'himalayas', 'linkedin', 'djinni', 'justjoin']).optional(),
});

export const FeedCountsQuery = z.object({
  source: z.enum(['telegram', 'talanto', 'himalayas', 'linkedin', 'djinni', 'justjoin']).optional(),
});
export const FeedCountsSchema = z.object({ new: z.number(), saved: z.number(), hidden: z.number() });
export type FeedCounts = z.infer<typeof FeedCountsSchema>;

export const FeedChannelSchema = z.object({
  id: IdSchema,
  ref: z.string(),
  title: z.string(),
  username: z.string(),
  lastCheckedAt: z.string().nullable(),
  lastError: z.string(),
});
export const FeedSettingsSchema = z.object({
  filter: FeedFilterSchema,
  channels: z.array(FeedChannelSchema),
  telegram: z.object({
    /** Администратор задал api_id/api_hash приложения Telegram. */
    available: z.boolean(),
    connected: z.boolean(),
    name: z.string().nullable(),
    /** Вход идёт: ждём код или пароль двухэтапной проверки. */
    pending: z.enum(['code', 'password']).nullable(),
    hint: z.string().nullable(),
    lastError: z.string().nullable(),
  }),
  talanto: z.object({ connected: z.boolean() }),
  lastScanAt: z.string().nullable(),
});
export type FeedSettings = z.infer<typeof FeedSettingsSchema>;
export const FeedChannelsBody = z.object({
  refs: z.array(z.string().trim().min(2).max(200)).max(100),
});
export const TgPhoneBody = z.object({
  phone: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s()-]/g, ''))
    .pipe(z.string().regex(/^\+\d{7,15}$/, 'Номер в международном формате: +34600000000')),
});
export const TgCodeBody = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, 'Код из Telegram — цифры'),
});
export const TgPasswordBody = z.object({ password: z.string().min(1).max(200) });

/* ---------- «Подготовить отклик» ---------- */

const GateSchema = z.object({ verdict: z.enum(GATES), note: z.string() });
export const PrepEvaluationSchema = z.object({
  lang: LetterLangSchema,
  company: z.string(),
  role: z.string(),
  scores: z.object({ skills: z.number(), experience: z.number(), career: z.number() }),
  notes: z.object({ skills: z.string(), experience: z.string(), career: z.string() }),
  gates: z.object({ location: GateSchema, salary: GateSchema, languages: GateSchema }),
  salaryMaxK: z.number().nullable(),
  requirements: z.array(
    z.object({ text: z.string(), must: z.boolean(), match: z.enum(MATCHES), evidence: z.string() }),
  ),
  strengths: z.array(z.string()),
  gaps: z.array(z.string()),
  summary: z.string(),
  overall: z.number(),
  verdict: z.enum(PREP_VERDICTS),
  vetoes: z.array(z.string()),
});
export type PrepEvaluationDto = z.infer<typeof PrepEvaluationSchema>;

export const PrepReviewSchema = z.object({
  issues: z.array(
    z.object({
      doc: z.enum(['cv', 'letter']),
      kind: z.enum(REVIEW_KINDS),
      quote: z.string(),
      problem: z.string(),
      fix: z.string(),
    }),
  ),
  missing: z.array(z.string()),
  summary: z.string(),
  /** Проверка фактов в коде: числа, компании и источники, которых нет в мастер-резюме. */
  warnings: z.array(z.string()),
  /** Строки мастер-резюме с цифрами результата, которых нет в резюме под вакансию. */
  dropped: z.array(z.string()),
  keywords: z.array(z.string()),
  /** Смежный опыт под требования без прямого подтверждения; used — уже в документах. */
  adjacent: z.array(
    z.object({
      requirement: z.string(),
      similar: z.string(),
      text: z.string(),
      source: z.string(),
      used: z.boolean(),
    }),
  ),
  notClaimed: z.array(z.string()),
  /** Вопросы кандидату: что он мог делать, но в мастер-резюме этого нет. */
  questions: z.array(z.string()),
});

const UsageStepSchema = z.object({
  model: z.string(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  calls: z.number().int(),
  /** Примерная стоимость в $; null — неизвестная модель. */
  costUsd: z.number().nullable(),
});
export const PrepSchema = z.object({
  applicationId: IdSchema,
  posting: z.string(),
  postingFlags: z.array(z.string()),
  invisibleRemoved: z.number().int(),
  lang: LetterLangSchema,
  evaluation: PrepEvaluationSchema.nullable(),
  evaluatedAt: z.string().nullable(),
  cvMd: z.string(),
  letter: z.string(),
  review: PrepReviewSchema.nullable(),
  documentsAt: z.string().nullable(),
  usage: z.object({
    evaluate: UsageStepSchema.nullable(),
    documents: UsageStepSchema.nullable(),
    totalTokens: z.number().int(),
    costUsd: z.number().nullable(),
  }),
  cvFileId: IdSchema.nullable(),
  letterFileId: IdSchema.nullable(),
  savedAt: z.string().nullable(),
  version: z.number().int(),
});
export type Prep = z.infer<typeof PrepSchema>;

export const PrepStateSchema = z.object({
  prep: PrepSchema.nullable(),
  /** Текст вакансии для начала: из ленты или заметок карточки. */
  postingDraft: z.string(),
  /** Мастер-резюме заполнено — без него подготовка недоступна. */
  masterCvReady: z.boolean(),
});
export type PrepState = z.infer<typeof PrepStateSchema>;

export const PrepEvaluateBody = z.object({
  posting: z.string().trim().min(80, 'Вставьте полный текст вакансии').max(40000),
});
export const PrepDocumentsBody = z.object({ lang: LetterLangSchema, extra: t(1000).default('') });
export const PrepPatchBody = z
  .object({ cvMd: z.string().max(40000), letter: z.string().max(20000) })
  .partial()
  .extend({ version: VersionSchema });
export const PrepPdfParams = z.object({ id: IdSchema, doc: z.enum(['cv', 'letter']) });
export const PostingTextSchema = z.object({ text: z.string() });
export const MasterCvFromFileBody = z.object({ fileId: IdSchema });
